import type { ReactNode } from "react";
import type { WalletProviderValue } from "@/modules/onboarding/constants/wallet-providers";

// Brand-colored payment marks. We don't ship official trademark vectors; these
// are recognizable color-forward lettermark badges — the brand *color* is the
// trust signal Egyptian sellers/buyers read fastest. Color lives ONLY inside
// the badge (never as system state), per the design's color discipline.
//
// Each badge desaturates to greyscale when its method is off and snaps to full
// color on activation — the "your register lights up" cue.

type Mark = { bg: string; fg: string; label: string };

const WALLET_MARKS: Record<WalletProviderValue, Mark> = {
  VODAFONE_CASH: { bg: "#E60000", fg: "#ffffff", label: "VF" },
  ORANGE_CASH: { bg: "#FF7900", fg: "#ffffff", label: "O" },
  ETISALAT_CASH: { bg: "#00A89C", fg: "#ffffff", label: "e&" },
  WE_PAY: { bg: "#7A1FA2", fg: "#ffffff", label: "WE" },
};

const INSTAPAY_MARK: Mark = { bg: "#5B2A86", fg: "#ffffff", label: "IP" };

function LogoBadge({
  mark,
  active,
  size = 28,
}: {
  mark: Mark;
  active: boolean;
  size?: number;
}) {
  return (
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center rounded-[8px] font-extrabold transition-[filter,opacity] duration-300"
      style={{
        width: size,
        height: size,
        background: mark.bg,
        color: mark.fg,
        fontSize: Math.round(size * 0.38),
        letterSpacing: "-0.03em",
        filter: active ? "none" : "grayscale(1)",
        opacity: active ? 1 : 0.5,
      }}
    >
      {mark.label}
    </span>
  );
}

export function WalletLogo({
  provider,
  active,
  size,
}: {
  provider: WalletProviderValue;
  active: boolean;
  size?: number;
}) {
  return <LogoBadge mark={WALLET_MARKS[provider]} active={active} size={size} />;
}

export function InstaPayLogo({
  active,
  size,
}: {
  active: boolean;
  size?: number;
}) {
  return <LogoBadge mark={INSTAPAY_MARK} active={active} size={size} />;
}

// A compact cluster of the four wallet marks for the "Mobile wallets" card
// header — the calm wall-of-badges recognition cue.
export function WalletCluster({ active }: { active: boolean }): ReactNode {
  return (
    <span className="inline-flex shrink-0 items-center -space-x-1.5 rtl:space-x-reverse">
      {(Object.keys(WALLET_MARKS) as WalletProviderValue[]).map((p) => (
        <span
          key={p}
          className="rounded-[7px] ring-2 ring-[color:var(--surface)]"
        >
          <WalletLogo provider={p} active={active} size={22} />
        </span>
      ))}
    </span>
  );
}

// Neutral (non-brand) glyph badges for COD and the Card teaser — these are
// generic icons, not payment brands, so they use the muted surface tokens.
function GlyphBadge({
  children,
  size = 28,
  muted = false,
}: {
  children: ReactNode;
  size?: number;
  muted?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={[
        "inline-flex shrink-0 items-center justify-center rounded-[8px] border",
        muted
          ? "border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-muted)]"
          : "border-[color:var(--success-500)]/30 bg-[color:var(--success-100)] text-[color:var(--success-500)]",
      ].join(" ")}
      style={{ width: size, height: size }}
    >
      {children}
    </span>
  );
}

export function CodLogo({ size }: { size?: number }) {
  return (
    <GlyphBadge size={size}>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <circle cx="12" cy="12" r="2.5" />
        <path d="M6 9v6M18 9v6" />
      </svg>
    </GlyphBadge>
  );
}

export function CardLogo({ size }: { size?: number }) {
  return (
    <GlyphBadge size={size} muted>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
      </svg>
    </GlyphBadge>
  );
}
